'use client';

import React from 'react';
import { NextPage } from 'next';
import Hooks from '@benzinga/hooks';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';
import { Space404Image } from './Space404Image';

export type CustomErrorProps = {
  statusCode: number;
  title?: string;
  subtitle?: string;
};

const ContentDefault: React.FC<{ statusCode: number; text?: string }> = ({ statusCode, text }) => {
  return (
    <>
      <p className="text-lg text-gray-400 mt-2.5 mb-5">{pageSubtitle(statusCode, text)}</p>
      <div>
        <a
          className="text-white bg-blue-500 font-semibold w-40 h-[50px] rounded inline-flex items-center justify-center"
          href="/"
        >
          <span>Go to Home</span>
        </a>
      </div>
    </>
  );
};

const ContentNotFound: React.FC = () => {
  return (
    <>
      <PageNotFoundImage />
      <h2 className="text-3xl text-gray-800 font-semibold uppercase mt-5">
        Oops! the page you were looking for could not be found
      </h2>
      <p className="text-lg text-gray-400 mt-2.5 mb-5">
        This page doesn't exist or was removed. We suggest you head back to home
      </p>
      <a
        className="text-white bg-blue-500 font-semibold w-40 h-[50px] rounded inline-flex items-center justify-center"
        href="/"
      >
        <span>Go to Home</span>
      </a>
    </>
  );
};

const pageTitle = (statusCode: number, title?: string) => {
  if (title) {
    return title;
  }
  if (statusCode === 410) {
    return 'Content Removed';
  }
  return `Uh-Oh! ${statusCode}`;
};

const pageSubtitle = (statusCode: number, subtitle?: string) => {
  if (subtitle) {
    return subtitle;
  }
  if (statusCode === 410) {
    return 'The requested resource is no longer available and has been removed permanently.';
  }
  return 'Oops! Something went wrong. Please try again soon or contact support@benzinga.com.';
};

export const CustomError: NextPage<CustomErrorProps> = ({ statusCode, subtitle, title }) => {
  const session = React.useContext(SessionContext);
  const trackError = statusCode => {
    if (statusCode === 404) {
      session.getManager(TrackingManager).trackErrorEvent('emit', {
        error_message: '404 Page Not Found',
      });
    } else if (statusCode === 410) {
      session.getManager(TrackingManager).trackErrorEvent('emit', {
        error_message: '410 Page Gone',
      });
    }
  };

  Hooks.useEffectDidMount(() => {
    trackError(statusCode);
  });

  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-132px)] bg-gray-50 mix-blend-normal p-[10px] text-center">
      <div className="flex items-center justify-center flex-col">
        {statusCode === 404 ? null : (
          <h2 className="text-3xl text-gray-800 font-semibold uppercase mt-5">{pageTitle(statusCode, title)}</h2>
        )}
        {statusCode === 404 ? <ContentNotFound /> : <ContentDefault statusCode={statusCode} text={subtitle} />}
      </div>
    </div>
  );
};

CustomError.getInitialProps = async ({ err, res }): Promise<CustomErrorProps> => {
  const statusCode = res ? res.statusCode : err?.statusCode ? err.statusCode : 404;
  return { statusCode };
};

export default CustomError;

const PageNotFoundImage = () => {
  return (
    <span className="inline-grid max-w-[450px] w-full p-2">
      <Space404Image />
    </span>
  );
};
